import { useMutation } from '@tanstack/react-query';
import { toast } from '@evaneos/b2b-ui';
import { produce } from 'immer';
import { useIntl } from 'react-intl';

import { queryClient } from '../../shared/providers/react-query-provider';
import { DossierId } from '../dossier/types';
import { markMessageAsNoResponseRequiredInCache } from '../dossier/cache-utils';

import { markAsNoResponseRequired, markAsResponseRequired } from './api';
import { NoResponseRequiredReason } from './types';
import { AllDiscussionMessagesResponse, getDiscussionMessagesQueryKey } from './queries';

export const useMarkAsNoResponseRequiredMutation = (dossierId: DossierId) => {
    const { formatMessage } = useIntl();

    return useMutation({
        mutationFn: ({
            messageId,
            reason,
        }: {
            messageId: number | null;
            reason: NoResponseRequiredReason;
        }) => markAsNoResponseRequired(dossierId, messageId, reason),
        onMutate: async () => {
            const discussionQueryKey = getDiscussionMessagesQueryKey(dossierId);
            await queryClient.cancelQueries({ queryKey: discussionQueryKey });

            const previousDiscussionData =
                queryClient.getQueryData<AllDiscussionMessagesResponse>(discussionQueryKey);

            queryClient.setQueryData<AllDiscussionMessagesResponse>(discussionQueryKey, (cur) => {
                if (!cur) return cur;
                return produce(cur, (draft) => {
                    draft.isResponseRequired = false;
                });
            });
            return { previousDiscussionData };
        },
        onSuccess: (_, variables) => {
            const translationKey =
                variables.reason === 'external_answer_done'
                    ? 'discussion.mark_as_answered_outside_rm_notification'
                    : 'discussion.mark_as_no_response_required_notification';

            toast.success(formatMessage({ id: translationKey }));
            markMessageAsNoResponseRequiredInCache(dossierId, { hasResponseRequired: false });
        },
        onError: (_, __, context) => {
            if (context?.previousDiscussionData) {
                queryClient.setQueryData(
                    getDiscussionMessagesQueryKey(dossierId),
                    context.previousDiscussionData,
                );
            }
            toast.error(formatMessage({ id: 'global.generic_error' }));
        },
    });
};

export const useMarkAsResponseRequiredMutation = (dossierId: DossierId) => {
    const { formatMessage } = useIntl();

    return useMutation({
        mutationFn: () => markAsResponseRequired(dossierId),
        onMutate: async () => {
            const discussionQueryKey = getDiscussionMessagesQueryKey(dossierId);
            await queryClient.cancelQueries({ queryKey: discussionQueryKey });

            const previousDiscussionData =
                queryClient.getQueryData<AllDiscussionMessagesResponse>(discussionQueryKey);

            queryClient.setQueryData<AllDiscussionMessagesResponse>(discussionQueryKey, (cur) => {
                if (!cur) return cur;
                return produce(cur, (draft) => {
                    draft.isResponseRequired = true;
                });
            });
            return { previousDiscussionData };
        },
        onSuccess: () => {
            markMessageAsNoResponseRequiredInCache(dossierId, { hasResponseRequired: true });
        },
        onError: (_, __, context) => {
            if (context?.previousDiscussionData) {
                queryClient.setQueryData(
                    getDiscussionMessagesQueryKey(dossierId),
                    context.previousDiscussionData,
                );
            }
            toast.error(formatMessage({ id: 'global.generic_error' }));
        },
    });
};
